import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import MapView from "../components/MapView";
import LocationTracker from "../components/LocationTracker";
import BottomNav from "../components/BottomNav";
import { useAuth } from "../lib/auth";
import { ArrowLeft, Users, Radio, Calendar, Navigation, Radar, Battery, BatteryLow, MoveRight, MapPin } from "lucide-react";
import { toast } from "sonner";
import { openDirections } from "../lib/directions";

export default function GroupDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/groups/${id}`);
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't load group");
    } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, [load]);

  useEffect(() => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setMe({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }),
        () => {},
        { enableHighAccuracy: true, maximumAge: 60000, timeout: 8000 }
      );
    }
  }, []);

  const onLocalUpdate = useCallback((pos) => {
    setMe({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy });
  }, []);

  const moveOut = async (device) => {
    if (!window.confirm(`Remove ${device.device_name} from "${data.group.name}"?`)) return;
    try {
      await api.patch(`/groups/${id}`, { remove_member_ids: [device.user_id] });
      toast.success("Removed from group");
      load();
    } catch { toast.error("Failed"); }
  };

  if (loading) {
    return <div className="min-h-screen bg-zinc-950 text-zinc-50 grid place-items-center"><div className="label-eyebrow">Loading group…</div></div>;
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-zinc-950 text-zinc-50 grid place-items-center p-6">
        <div className="text-center">
          <div className="font-display font-black text-2xl">Group unavailable</div>
          <div className="text-sm text-zinc-400 mt-2">{error || "Couldn't load this group."}</div>
          <button onClick={() => navigate(-1)} className="mt-5 px-5 py-2.5 rounded-full border border-white/10 font-bold">Go back</button>
        </div>
      </div>
    );
  }

  const { group, devices, master } = data;
  const isMasterView = user?.account_role !== "node";
  const backTo = isMasterView ? "/hub" : "/group";

  // Convert devices array → MapView contacts shape
  const mapContacts = devices
    .filter((d) => d.latest && d.latest.lat != null)
    .map((d) => ({
      user_id: d.user_id,
      name: d.device_name || d.name,
      relation: d.relation || "member",
      latest: d.latest,
      picture: d.picture,
      distance_miles: d.distance_miles,
    }));
  // If master, also include themselves on the map (their own marker)
  if (isMasterView && master?.latest) {
    mapContacts.push({
      user_id: master.user_id,
      name: master.name,
      relation: "master",
      latest: master.latest,
      picture: master.picture,
    });
  }

  return (
    <div className="relative h-screen w-screen overflow-hidden bg-zinc-950 text-zinc-50">
      <LocationTracker enabled={user?.share_location !== false} onUpdate={onLocalUpdate} />

      <div className="absolute inset-0 z-0">
        <MapView
          me={me}
          contacts={mapContacts}
          geofences={[]}
          height="100vh"
        />
      </div>

      {/* Header */}
      <div className="absolute top-4 left-4 right-4 z-20 flex items-start gap-3">
        <Link
          to={backTo}
          data-testid="group-back"
          className="glass size-12 rounded-full grid place-items-center hover:bg-zinc-900/80"
        >
          <ArrowLeft size={18}/>
        </Link>
        <div className="glass rounded-2xl px-4 py-3 flex items-center gap-3 flex-1 min-w-0">
          <div className={`size-9 rounded-full grid place-items-center ${group.is_active ? "bg-[#34C759]/15" : "bg-zinc-800"}`}>
            <Users size={18} className={group.is_active ? "text-[#34C759]" : "text-zinc-400"}/>
          </div>
          <div className="min-w-0">
            <div className="label-eyebrow flex items-center gap-1.5">
              {group.is_active ? (
                <><Radio size={10} className="text-[#34C759]"/><span className="text-[#34C759]">Live</span></>
              ) : group.status === "scheduled" ? (
                <><Calendar size={10} className="text-[#FFCC00]"/><span className="text-[#FFCC00]">Scheduled</span></>
              ) : (
                <span className="text-zinc-500">Off</span>
              )}
              <span className="text-zinc-500">· {devices.length} {devices.length === 1 ? "device" : "devices"}</span>
            </div>
            <div className="font-display text-lg font-bold truncate" data-testid="group-title">{group.name}</div>
          </div>
        </div>
      </div>

      {/* Recenter */}
      <div className="absolute right-4 top-24 z-20 flex flex-col gap-3">
        <button
          data-testid="group-recenter"
          onClick={() => me && window.dispatchEvent(new CustomEvent("recenter"))}
          className="glass size-12 rounded-full grid place-items-center hover:bg-zinc-900/80"
          title="Recenter"
        >
          <MapPin size={18} />
        </button>
      </div>

      {/* Bottom sheet: members */}
      <div className="absolute left-4 right-4 bottom-24 z-20">
        <div className="glass rounded-2xl p-3 max-h-[40vh] overflow-y-auto">
          <div className="px-2 py-1 label-eyebrow">Members · {devices.length}</div>
          {devices.length === 0 ? (
            <div className="px-3 py-6 text-sm text-zinc-500">No devices in this group yet.</div>
          ) : (
            <div className="divide-y divide-white/5">
              {devices.map((d) => (
                <div key={d.user_id} className="px-2 py-3 flex items-center gap-3" data-testid={`member-row-${d.user_id}`}>
                  <div className="relative">
                    <div className="size-10 rounded-full bg-zinc-800 overflow-hidden grid place-items-center font-display font-bold">
                      {d.picture ? <img src={d.picture} alt="" className="w-full h-full object-cover" /> : (d.device_name || "?").slice(0,1)}
                    </div>
                    <span
                      className={`absolute -bottom-0.5 -right-0.5 size-3 rounded-full border-2 border-zinc-950 ${d.online ? "bg-[#34C759]" : "bg-zinc-600"}`}
                      title={d.online ? "Online" : "Offline"}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{d.device_name || d.name}</div>
                    <div className="text-[11px] text-zinc-500 truncate flex items-center gap-1.5">
                      <span>{d.online ? "Online" : d.last_seen ? `Last seen ${timeAgo(d.last_seen)}` : "No location yet"}</span>
                      {d.battery != null && (
                        <span className="inline-flex items-center gap-0.5">
                          · {d.battery <= 20 ? <BatteryLow size={10} className="text-[#FF3B30]"/> : <Battery size={10}/>}
                          {d.battery}%
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-sm">
                      {d.distance_miles == null ? "—" :
                        d.distance_miles < 0.19 ? `${Math.round(d.distance_miles*5280)} ft`
                          : `${d.distance_miles.toFixed(2)} mi`}
                    </div>
                    <div className="flex items-center justify-end gap-1.5 mt-1">
                      <Link
                        to={`/find/${d.user_id}`}
                        data-testid={`group-find-${d.user_id}`}
                        className="text-[10px] font-bold uppercase tracking-wider text-[#007AFF] inline-flex items-center gap-1"
                      >
                        <Radar size={11}/> Find
                      </Link>
                      {d.latest && (
                        <button
                          data-testid={`group-navigate-${d.user_id}`}
                          onClick={() => openDirections({ lat: d.latest.lat, lng: d.latest.lng, label: d.device_name, mode: "driving" })}
                          className="text-[10px] font-bold uppercase tracking-wider text-[#34C759] inline-flex items-center gap-1 ml-1"
                        >
                          <Navigation size={11}/> Go
                        </button>
                      )}
                      {isMasterView && (
                        <button
                          data-testid={`remove-from-group-${d.user_id}`}
                          onClick={() => moveOut(d)}
                          className="text-[10px] font-bold uppercase tracking-wider text-[#FF3B30] inline-flex items-center gap-1 ml-1"
                          title="Remove from group"
                        >
                          <MoveRight size={11}/> Out
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <BottomNav />
    </div>
  );
}

function timeAgo(iso) {
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff/60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff/3600)}h ago`;
    return `${Math.floor(diff/86400)}d ago`;
  } catch { return ""; }
}
